/**
 * Wires the pure {@link PendingTradeRegistry} to the live NATS stream so a
 * submitted trade can be confirmed the instant the indexer publishes it.
 *
 * Two venues, two subjects, two proto messages — both normalize to
 * {@link NormalizedTradeActivity}:
 *   - bonding curve → `morfi.market.{market}.user.{wallet}.trade.v0`  (MarketTrade, has signature)
 *   - graduated AMM → `morfi.amm.*.user.{wallet}.activity.v0` (AmmUserActivity, NO signature)
 *
 * Subscriptions go through the registry's `acquireEntity` observer primitive
 * (the same path the codegen hooks use) — it triggers the wire SUB on the 0→1
 * interest transition and tears it down on release. Per concrete subject we
 * hold ONE shared observer (refcounted), feeding the single module registry so
 * FIFO single-consumption holds across concurrent identical trades.
 *
 * Platform-agnostic: only `@bufbuild/protobuf`, `@morfi/proto`, the registry,
 * and pure logic. No web/RN APIs.
 */

import { fromBinary } from '@bufbuild/protobuf';
import { ammActivitySubject, marketTradeSubject } from '@morfi/nats-client';
import { AmmUserActivityKind, AmmUserActivitySchema, MarketTradeSchema, Side } from '@morfi/proto';
import { getRegistrySingleton } from '../../nats/registry/subject-registry.js';
import { hashDiagnosticEntity } from '../diagnostics/lifecycle.js';
import {
  type NormalizedTradeActivity,
  PendingTradeRegistry,
  type TradeMatch,
} from './pending-trade-registry.js';

/** Allow this many seconds of client-vs-chain clock skew when gating events by
 *  `ts >= submitStart`. Kept small: the structural protections (live
 *  subscription, FIFO, sequenceId dedup) are primary; this is a belt. */
const CLOCK_SKEW_TOLERANCE_SEC = 3n;

export interface TradeWatchInput {
  wallet: string;
  market: string;
  outcomeIdx: number;
  side: 'buy' | 'sell';
  /** 'amm' for graduated (cp-swap) markets, 'bc' otherwise. Selects the subject + decoder. */
  venue: 'bc' | 'amm';
  /** Trade submit wall-clock (ms epoch); gates out stale prior-trade events. */
  submitStartMs: number;
  /** Optional observability sink. It receives only bounded fields and hashed identifiers. */
  debug?: (msg: string, ctx: Record<string, unknown>) => void;
}

export interface TradeWatchHandle {
  /** Resolves with the match on first hit, or null when cancelled unmatched. */
  matched: Promise<TradeMatch | null>;
  /** Release the subscription refcount + drop the pending entry. Idempotent. */
  cancel: () => void;
}

const registry = new PendingTradeRegistry();

interface SubRef {
  count: number;
  release: () => void;
  rejectionSinks: Set<(reason: TradeWatchRejectionReason) => void>;
}
const subRefs = new Map<string, SubRef>();

export type TradeWatchRejectionReason =
  | 'payload_not_bytes'
  | 'protobuf_decode_failed'
  | 'non_trade_kind'
  | 'invalid_side'
  | 'missing_identity'
  | 'invalid_sequence_id';

type DecodeResult =
  | { activity: NormalizedTradeActivity; reason: null }
  | { activity: null; reason: TradeWatchRejectionReason };

function hex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

function sideToString(side: Side): 'buy' | 'sell' | null {
  if (side === Side.BUY) return 'buy';
  if (side === Side.SELL) return 'sell';
  return null;
}

function decodeMarketTrade(bytes: Uint8Array, market: string): DecodeResult {
  let m: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
  try {
    m = fromBinary(MarketTradeSchema, bytes);
  } catch {
    return { activity: null, reason: 'protobuf_decode_failed' };
  }
  const side = sideToString(m.side);
  // MarketTrade is intentionally market-less: the exact market is already
  // fixed by the concrete subject this watch acquires.
  if (side === null) return { activity: null, reason: 'invalid_side' };
  if (market === '' || m.actor === '') {
    return { activity: null, reason: 'missing_identity' };
  }
  if (m.sequenceId.length !== 15) {
    return { activity: null, reason: 'invalid_sequence_id' };
  }
  return {
    activity: {
      actor: m.actor,
      market,
      outcomeIdx: m.outcomeIdx,
      side,
      ts: m.ts,
      signature: m.signature.length > 0 ? m.signature : null,
      sequenceIdHex: hex(m.sequenceId),
    },
    reason: null,
  };
}

function decodeAmmActivity(bytes: Uint8Array): DecodeResult {
  let m: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
  try {
    m = fromBinary(AmmUserActivitySchema, bytes);
  } catch {
    return { activity: null, reason: 'protobuf_decode_failed' };
  }
  if (m.kind !== AmmUserActivityKind.TRADE) {
    return { activity: null, reason: 'non_trade_kind' };
  }
  const side = sideToString(m.side);
  // A malformed/partial event with missing routing identity must NOT
  // participate in matching — else it could cross-match an unrelated pending
  // entry. The optional outcome index preserves field presence even for index
  // zero.
  if (side === null) return { activity: null, reason: 'invalid_side' };
  if (m.market === '' || m.actor === '' || m.outcomeIdx === undefined) {
    return { activity: null, reason: 'missing_identity' };
  }
  if (m.sequenceId.length !== 15) {
    return { activity: null, reason: 'invalid_sequence_id' };
  }
  return {
    activity: {
      actor: m.actor,
      market: m.market,
      outcomeIdx: m.outcomeIdx,
      side,
      ts: m.ts,
      // AmmUserActivity carries no signature; Turnkey reconciles it later.
      signature: null,
      sequenceIdHex: hex(m.sequenceId),
    },
    reason: null,
  };
}

function acquireSub(
  subject: string,
  decode: (b: Uint8Array) => DecodeResult,
  rejectionSink?: (reason: TradeWatchRejectionReason) => void,
): void {
  const existing = subRefs.get(subject);
  if (existing !== undefined) {
    existing.count += 1;
    if (rejectionSink !== undefined) existing.rejectionSinks.add(rejectionSink);
    return;
  }
  const rejectionSinks = new Set<(reason: TradeWatchRejectionReason) => void>();
  if (rejectionSink !== undefined) rejectionSinks.add(rejectionSink);
  const handle = getRegistrySingleton().acquireEntity(
    [subject],
    (_subject: string, payload: unknown): void => {
      if (!(payload instanceof Uint8Array)) {
        for (const sink of rejectionSinks) sink('payload_not_bytes');
        return;
      }
      const decoded = decode(payload);
      if (decoded.activity === null) {
        for (const sink of rejectionSinks) sink(decoded.reason);
        return;
      }
      registry.onActivity(decoded.activity);
    },
    ['__morfi_tx_watch', subject],
  );
  subRefs.set(subject, {
    count: 1,
    release: (): void => handle.release(),
    rejectionSinks,
  });
}

function releaseSub(
  subject: string,
  rejectionSink?: (reason: TradeWatchRejectionReason) => void,
): void {
  const ref = subRefs.get(subject);
  if (ref === undefined) return;
  if (rejectionSink !== undefined) ref.rejectionSinks.delete(rejectionSink);
  ref.count -= 1;
  if (ref.count <= 0) {
    ref.release();
    subRefs.delete(subject);
  }
}

export function resolveTradeWatchSubject(
  input: Pick<TradeWatchInput, 'venue' | 'wallet' | 'market'>,
): string {
  return input.venue === 'amm'
    ? ammActivitySubject('*', input.wallet)
    : marketTradeSubject(input.market, input.wallet);
}

/**
 * Arm a watch for one in-flight trade. The returned promise resolves the moment
 * the indexer publishes a matching trade (or null when `cancel()` is called
 * before any match). The caller MUST call `cancel()` when the trade settles.
 */
export function registerTradeWatch(input: TradeWatchInput): TradeWatchHandle {
  const subject = resolveTradeWatchSubject(input);
  const decode =
    input.venue === 'amm'
      ? decodeAmmActivity
      : (bytes: Uint8Array): DecodeResult => decodeMarketTrade(bytes, input.market);
  const rejectionSink =
    input.debug === undefined
      ? undefined
      : (reason: TradeWatchRejectionReason): void => {
          input.debug?.('nats-watch activity rejected', {
            reason,
            venue: input.venue,
          });
        };

  input.debug?.('nats-watch armed', {
    walletHash: hashDiagnosticEntity(input.wallet),
    marketHash: hashDiagnosticEntity(input.market),
    outcomeIdx: input.outcomeIdx,
    side: input.side,
    venue: input.venue,
  });

  acquireSub(subject, decode, rejectionSink);

  const sinceTsSec = BigInt(Math.floor(input.submitStartMs / 1000)) - CLOCK_SKEW_TOLERANCE_SEC;
  const { id, matched } = registry.enqueue({
    wallet: input.wallet,
    market: input.market,
    outcomeIdx: input.outcomeIdx,
    side: input.side,
    sinceTsSec,
  });

  let released = false;
  const cancel = (): void => {
    if (released) return;
    released = true;
    registry.cancel(id);
    releaseSub(subject, rejectionSink);
  };

  return { matched, cancel };
}

/** Test-only seam: the module's pending registry (for unit assertions). */
export function __testRegistry(): PendingTradeRegistry {
  return registry;
}

export function __testDecodeAmmActivity(bytes: Uint8Array): DecodeResult {
  return decodeAmmActivity(bytes);
}

export function __testDecodeMarketTrade(bytes: Uint8Array, market: string): DecodeResult {
  return decodeMarketTrade(bytes, market);
}
